import { useState, useEffect } from "react";
import HorseRace from "./games/HorseRace.jsx";
import Basketball from "./games/Basketball.jsx";
import Football from "./games/Football.jsx";
import Shooting from "./games/Shooting.jsx";
import Claw from "./games/Claw.jsx";
import Wheel from "./games/Wheel.jsx";
import RaceCars from "./games/RaceCars.jsx";
import Mole from "./games/Mole.jsx";
import Cans from "./games/Cans.jsx";
import Hangtime from "./games/Hangtime.jsx";
import Punch from "./games/Punch.jsx";
import DuckPond from "./games/DuckPond.jsx";
import Pusher from "./games/Pusher.jsx";
import Hammer from "./games/Hammer.jsx";
import Bump from "./games/Bump.jsx";
import PrizeRoom from "./PrizeRoom.jsx";
import Exchange from "./Exchange.jsx";
import Leaderboard from "./Leaderboard.jsx";
import Model3D from "./Model3D.jsx";
import CoinImg from "./CoinImg.jsx";
import { loadPrizes, saveAllPrizes } from "./prizes.js";
import { getName, setName, validateName } from "./profile.js";
import { syncScore } from "./board.js";
import { t, getLang, setLang, LANGS } from "./i18n.js";

/* SVG flags — Windows renders flag emojis as plain letters ("DK"/"GB"),
   so the language picker draws real flags instead. */
function FlagDK() {
  return (
    <svg viewBox="0 0 37 28" width="30" height="22" aria-hidden="true">
      <rect width="37" height="28" fill="#C8102E" />
      <rect x="12" width="5" height="28" fill="#fff" />
      <rect y="11.5" width="37" height="5" fill="#fff" />
    </svg>
  );
}
function FlagUK() {
  return (
    <svg viewBox="0 0 36 28" width="30" height="22" aria-hidden="true">
      <rect width="36" height="28" fill="#012169" />
      <path d="M0,0 36,28 M36,0 0,28" stroke="#fff" strokeWidth="5.5" />
      <path d="M0,0 36,28 M36,0 0,28" stroke="#C8102E" strokeWidth="2.2" />
      <path d="M18,0 V28 M0,14 H36" stroke="#fff" strokeWidth="9" />
      <path d="M18,0 V28 M0,14 H36" stroke="#C8102E" strokeWidth="5" />
    </svg>
  );
}
function FlagDE() {
  return (
    <svg viewBox="0 0 36 28" width="30" height="22" aria-hidden="true">
      <rect width="36" height="28" fill="#000" />
      <rect y="9.33" width="36" height="9.33" fill="#DD0000" />
      <rect y="18.66" width="36" height="9.34" fill="#FFCC00" />
    </svg>
  );
}
function FlagIT() {
  return (
    <svg viewBox="0 0 36 28" width="30" height="22" aria-hidden="true">
      <rect width="12" height="28" fill="#009246" />
      <rect x="12" width="12" height="28" fill="#fff" />
      <rect x="24" width="12" height="28" fill="#CE2B37" />
    </svg>
  );
}
function FlagFR() {
  return (
    <svg viewBox="0 0 36 28" width="30" height="22" aria-hidden="true">
      <rect width="12" height="28" fill="#0055A4" />
      <rect x="12" width="12" height="28" fill="#fff" />
      <rect x="24" width="12" height="28" fill="#EF4135" />
    </svg>
  );
}
function FlagES() {
  return (
    <svg viewBox="0 0 36 28" width="30" height="22" aria-hidden="true">
      <rect width="36" height="28" fill="#AA151B" />
      <rect y="7" width="36" height="14" fill="#F1BF00" />
    </svg>
  );
}
const FLAGS = { da: FlagDK, en: FlagUK, de: FlagDE, it: FlagIT, fr: FlagFR, es: FlagES };

/* Slå sækken-flise-ikon: håndtegnet SVG (ingen emoji) — hængende læder-
   boksesæk i kæder m. kryds-lap, samme familie som spillets canvas-tegning. */
function PunchIcon({ size = 100 }) {
  return (
    <svg viewBox="0 0 100 110" width={size} height={size * 1.1} aria-hidden="true" className="cv-flat">
      <path d="M50 2 L38 26 M50 2 L62 26 M50 2 L50 24" stroke="#9aa1a8" strokeWidth="3" strokeLinecap="round" strokeDasharray="4 3" />
      <rect x="30" y="24" width="40" height="76" rx="17" fill="url(#pbag)" stroke="#1e0d04" strokeWidth="2" />
      <line x1="34" y1="36" x2="66" y2="36" stroke="#f0d67a" strokeWidth="1.6" strokeDasharray="3 3" opacity="0.7" />
      <line x1="34" y1="88" x2="66" y2="88" stroke="#f0d67a" strokeWidth="1.6" strokeDasharray="3 3" opacity="0.7" />
      <rect x="42" y="48" width="16" height="16" fill="none" stroke="#1e0d04" strokeWidth="1.8" />
      <path d="M42 48 L58 64 M58 48 L42 64" stroke="#1e0d04" strokeWidth="1.8" />
      <defs>
        <linearGradient id="pbag" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#3a2010" />
          <stop offset="0.4" stopColor="#7a4a24" />
          <stop offset="1" stopColor="#2c180a" />
        </linearGradient>
      </defs>
    </svg>
  );
}
/* Andedam-flise-ikon: håndtegnet gummiand på vandringe (ingen emoji). */
function DuckIcon({ size = 100 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true" className="cv-flat">
      <ellipse cx="50" cy="78" rx="38" ry="9" fill="#2d6379" />
      <ellipse cx="50" cy="78" rx="27" ry="6" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.6" />
      <ellipse cx="50" cy="80" rx="16" ry="3.6" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.4" />
      <ellipse cx="47" cy="62" rx="26" ry="17" fill="url(#dkbody)" />
      <path d="M26 57 Q14 48 24 42 Q28 50 33 53 Z" fill="url(#dkbody)" />
      <circle cx="66" cy="42" r="14" fill="url(#dkhead)" />
      <path d="M78 40 Q92 42 79 47 Q77 43 78 40 Z" fill="#f08b1f" />
      <circle cx="70" cy="38" r="2.8" fill="#2b1d0a" />
      <circle cx="71" cy="37" r="1" fill="#fff" />
      <ellipse cx="42" cy="49" rx="7" ry="3.4" fill="#9aa4ae" transform="rotate(-14 42 49)" />
      <path d="M40 60 Q48 66 56 60" stroke="rgba(160,110,20,0.55)" strokeWidth="2.4" fill="none" />
      <defs>
        <linearGradient id="dkbody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffd94f" />
          <stop offset="1" stopColor="#eab225" />
        </linearGradient>
        <radialGradient id="dkhead" cx="0.4" cy="0.35" r="1">
          <stop offset="0" stopColor="#ffe27a" />
          <stop offset="1" stopColor="#f0bf35" />
        </radialGradient>
      </defs>
    </svg>
  );
}

/* Coin Pusher-flise-ikon: to hylder m. mønter og pusher (ingen emoji). */
function PusherIcon({ size = 100 }) {
  const coin = (x, y, gold) => (
    <g key={x + "-" + y}>
      <ellipse cx={x} cy={y + 2} rx="9" ry="5" fill={gold ? "#8a5f1e" : "#5f6a74"} />
      <ellipse cx={x} cy={y} rx="9" ry="5" fill={gold ? "url(#pgold)" : "url(#psilv)"} stroke={gold ? "#8a5f1e" : "#5f6a74"} strokeWidth="0.8" />
    </g>
  );
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true" className="cv-flat">
      <rect x="8" y="6" width="84" height="88" rx="8" fill="#1a2246" stroke="#c9a24b" strokeWidth="2.5" />
      <rect x="16" y="14" width="68" height="10" rx="3" fill="#8a3d36" />
      <polygon points="20,34 80,34 84,52 16,52" fill="#2d3f6a" />
      {coin(38, 44, false)}{coin(58, 46, true)}{coin(48, 40, false)}
      <rect x="16" y="52" width="68" height="4" fill="#0a0f22" />
      <polygon points="16,60 84,60 88,80 12,80" fill="#25355e" />
      {coin(32, 72, true)}{coin(52, 74, false)}{coin(68, 70, true)}{coin(44, 66, true)}
      <rect x="12" y="80" width="76" height="4" fill="#0a0f22" />
      <rect x="26" y="86" width="48" height="6" rx="2" fill="rgba(255,215,94,0.4)" stroke="#c9a24b" strokeWidth="1" />
      <defs>
        <linearGradient id="pgold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f7dd8f" />
          <stop offset="1" stopColor="#b8862a" />
        </linearGradient>
        <linearGradient id="psilv" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f2f5f8" />
          <stop offset="1" stopColor="#98a3ad" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* Styrkeprøve-flise-ikon: tårn m. klokke + hammer (ingen emoji).
   viewBox rummer hammerens rotation (hovedet når x≈-15 — var halvt klippet) */
function HammerIcon({ size = 100 }) {
  return (
    <svg viewBox="-20 0 124 112" width={size} height={size * 1.1} aria-hidden="true" className="cv-flat">
      <rect x="58" y="22" width="12" height="74" rx="3" fill="#5f3d1f" stroke="#3a2412" strokeWidth="1.5" />
      <rect x="60.5" y="82" width="7" height="8" fill="#c0392b" />
      <rect x="60.5" y="70" width="7" height="8" fill="#c0392b" />
      <rect x="60.5" y="58" width="7" height="8" fill="#e08a2e" />
      <rect x="60.5" y="46" width="7" height="8" fill="#e08a2e" />
      <rect x="60.5" y="34" width="7" height="8" fill="#3fae46" />
      <circle cx="64" cy="16" r="10" fill="url(#hbell)" stroke="#7a5510" strokeWidth="1.6" />
      <circle cx="64" cy="24" r="2.2" fill="#7a5510" />
      <ellipse cx="64" cy="99" rx="20" ry="6" fill="#4d545c" stroke="#c9a24b" strokeWidth="1.6" />
      <g transform="rotate(-38 26 74)">
        <rect x="23" y="40" width="6" height="42" rx="2.5" fill="#8a5f36" />
        <rect x="12" y="26" width="28" height="16" rx="5" fill="url(#hhead)" stroke="#3a2412" strokeWidth="1.6" />
      </g>
      <defs>
        <radialGradient id="hbell" cx="0.35" cy="0.3" r="1">
          <stop offset="0" stopColor="#ffe9a1" />
          <stop offset="1" stopColor="#c9982e" />
        </radialGradient>
        <linearGradient id="hhead" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a9713d" />
          <stop offset="1" stopColor="#5a3416" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* Radiobil-flise-ikon: bamse-radiobil set fra oven m. kromkofanger (ingen emoji). */
function BumpIcon({ size = 100 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true" className="cv-flat">
      <circle cx="50" cy="52" r="40" fill="#2c3440" stroke="#4fd8e8" strokeWidth="2.5" />
      <circle cx="50" cy="52" r="27" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
      <circle cx="50" cy="50" r="20" fill="url(#bchrome)" />
      <circle cx="50" cy="50" r="15.5" fill="#c8352e" />
      <ellipse cx="45" cy="44" rx="7" ry="4.5" fill="rgba(255,255,255,0.3)" transform="rotate(-30 45 44)" />
      <circle cx="44" cy="43" r="3.4" fill="#a9713d" />
      <circle cx="56" cy="43" r="3.4" fill="#a9713d" />
      <circle cx="50" cy="49" r="8.6" fill="#a9713d" />
      <ellipse cx="50" cy="52" rx="4.4" ry="3.4" fill="#f0e0c0" />
      <circle cx="47" cy="47.4" r="1.3" fill="#2b1d0a" />
      <circle cx="53" cy="47.4" r="1.3" fill="#2b1d0a" />
      <circle cx="50" cy="51" r="1.3" fill="#2b1d0a" />
      <circle cx="50" cy="31.6" r="2.6" fill="#ffe9a1" />
      <defs>
        <linearGradient id="bchrome" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e8ecf0" />
          <stop offset="0.5" stopColor="#9aa4ae" />
          <stop offset="1" stopColor="#5f6a74" />
        </linearGradient>
      </defs>
    </svg>
  );
}

const LANG_LABELS = { da: "Dansk", en: "English", de: "Deutsch", it: "Italiano", fr: "Français", es: "Español" };

/* The TEDDY CHASE marquee sign, drawn in pure CSS (gold gradient + neon-tube
   stroke + flicker). A rendered PNG can take over again later by reintroducing
   the <img> track (mix-blend-mode:screen, see .cv-title-img). */
function TitleMark() {
  return (
    <div className="cv-title">
      <h1 className="cv-title-text" data-text="TEDDY CHASE">TEDDY CHASE</h1>
    </div>
  );
}

/* Pick / edit the player name, with a profanity check. */
function NameModal({ onDone, onCancel }) {
  const [value, setValue] = useState(getName());
  const [error, setError] = useState("");
  const save = () => {
    const err = validateName(value);
    if (err) { setError(err); return; }
    const name = value.trim();
    setName(name);
    // the whole collection follows the player: re-own every prize so a
    // rename never strands them under the old name
    saveAllPrizes(loadPrizes().map((p) => ({ ...p, owner: name })));
    onDone(name);
  };
  return (
    <div className="cv-overlay">
      <div className="cv-card">
        <div className="cv-ribbon">{t("name_q")}</div>
        <p className="cv-namenote">{t("name_note")} {t("name_device_note")}</p>
        <input
          className="cv-nameinput"
          value={value}
          maxLength={12}
          placeholder={t("name_ph")}
          onChange={(e) => { setValue(e.target.value); setError(""); }}
          onKeyDown={(e) => e.key === "Enter" && save()}
          autoFocus
        />
        {error && <p className="cv-nameerr">{t(error)}</p>}
        <button className="cv-cta" onClick={save}>{t("name_save")}</button>
        {getName() && <button className="cv-cta alt" onClick={onCancel}>{t("back").replace("‹ ", "")}</button>}
      </div>
    </div>
  );
}

/* Views reachable via ?g= deep links (galoppen.dk redirects to ?g=horse,
   shareable per-booth links, TWA shortcuts). */
const VIEWS = ["horse", "basket", "football", "shoot", "claw", "wheel", "race", "mole", "cans", "hang", "punch", "duck", "pusher", "hammer", "bump", "prizes", "exchange", "board"];

export default function App() {
  const [view, setView] = useState("menu"); // menu | horse | basket | football | shoot | claw | wheel | prizes | board
  const [lang, setLangState] = useState(getLang());
  const [askName, setAskName] = useState(false);
  const [pending, setPending] = useState(null);
  const prizeCount = view === "menu" ? loadPrizes().length : 0;

  /* the static SEO landing section (index.html #landing) belongs to the
     front page only — hide it while a booth is open. Always land at the top
     when the view changes: entering a booth with the menu's scroll position
     left the header off-screen, and the canvas (touch-action:none) made it
     nearly impossible to scroll back up. */
  useEffect(() => {
    document.body.classList.toggle("cv-ingame", view !== "menu");
    window.scrollTo(0, 0);
  }, [view]);

  /* the tab/bookmark title follows the active language (the static title in
     index.html is the Danish crawl fallback) */
  useEffect(() => {
    document.title = t("doc_title");
    // den statiske SEO-landing (#landing, uden for React) følger også sproget:
    // hvert element med data-i18n får sin oversatte tekst (dansk = fallback)
    document.querySelectorAll("#landing [data-i18n]").forEach((el) => {
      el.textContent = t(el.getAttribute("data-i18n"));
    });
    const priv = document.querySelector("#landing .foot a");
    if (priv) priv.setAttribute("href", lang === "da" ? "/privacy/" : `/privacy/${lang}.html`);
  }, [lang]);

  const [langOpen, setLangOpen] = useState(false);
  const pickLang = (l) => {
    setLang(l);
    setLangState(l);
    setLangOpen(false);
  };

  /* Change view and keep the URL in sync (?g=<view>, menu = clean URL). */
  const go = (target) => {
    setView(target);
    let url = target === "menu" ? window.location.pathname : `${window.location.pathname}?g=${target}`;
    // challenge-parametre skal overleve deep-link-rewriten (Slå sækken + Styrkeprøven)
    // (?g=punch&ch=740&by=Navn / ?g=hammer&ch=920&by=Navn) — findes kun i URL'en ved første load
    if (target === "punch" || target === "hammer") {
      const q = new URLSearchParams(window.location.search);
      const ch = q.get("ch"), by = q.get("by");
      if (ch && by) url += `&ch=${encodeURIComponent(ch)}&by=${encodeURIComponent(by)}`;
    }
    try { window.history.replaceState(null, "", url); } catch {}
  };

  /* Games need a name (leaderboard + race track); ask once, then continue. */
  const play = (target) => {
    if (!getName() && target !== "prizes" && target !== "board" && target !== "exchange") {
      setPending(target);
      setAskName(true);
      return;
    }
    go(target);
  };

  /* Deep link: ?g=horse opens the booth directly (name gate still applies). */
  useEffect(() => {
    const g = new URLSearchParams(window.location.search).get("g");
    if (VIEWS.includes(g)) play(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exit = () => {
    go("menu");
    syncScore();   // push fresh totals to the global board (fire and forget)
  };

  return (
    <div key={lang}>
      {/* fonts loades i index.html <head> (preconnect + stylesheet), så de er
          på vej før React booter — også til den statiske landing-tekst */}
      {view === "horse" && <HorseRace onExit={exit} />}
      {view === "basket" && <Basketball onExit={exit} />}
      {view === "football" && <Football onExit={exit} />}
      {view === "shoot" && <Shooting onExit={exit} />}
      {view === "claw" && <Claw onExit={exit} />}
      {view === "wheel" && <Wheel onExit={exit} />}
      {view === "race" && <RaceCars onExit={exit} />}
      {view === "mole" && <Mole onExit={exit} />}
      {view === "cans" && <Cans onExit={exit} />}
      {view === "hang" && <Hangtime onExit={exit} />}
      {view === "punch" && <Punch onExit={exit} />}
      {view === "duck" && <DuckPond onExit={exit} />}
      {view === "pusher" && <Pusher onExit={exit} />}
      {view === "hammer" && <Hammer onExit={exit} />}
      {view === "bump" && <Bump onExit={exit} />}
      {view === "prizes" && <PrizeRoom onExit={exit} />}
      {view === "exchange" && <Exchange onExit={exit} />}
      {view === "board" && <Leaderboard onExit={exit} />}
      {view === "menu" && (
        /* data-nosnippet: Googles snippet skal komme fra landing-teksten/meta
           description, ikke fra flisernes navne + emoji-fallbacks (gav en grim
           "Hestevæddeløb ; ⚽ Fodbold ;"-linje i søgeresultatet) */
        <div className="cv-root" data-nosnippet="">
          <style>{css}</style>
          <div className="cv-scene" aria-hidden="true" />

          <div className="cv-langbox">
            <button className="cv-flag" onClick={() => setLangOpen((o) => !o)} aria-label="Language">
              {(() => { const F = FLAGS[lang] || FlagDK; return <F />; })()}
            </button>
            {langOpen && (
              <div className="cv-langmenu">
                {LANGS.map((l) => {
                  const F = FLAGS[l];
                  return (
                    <button key={l} className={"cv-langitem" + (l === lang ? " on" : "")} onClick={() => pickLang(l)}>
                      <F /> <span>{LANG_LABELS[l]}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <header className="cv-booth">
            <div className="cv-lights" aria-hidden="true">
              {Array.from({ length: 22 }).map((_, i) => (
                <span key={i} className={"cv-bulb" + (i % 3 === 0 ? " warm" : "")} />
              ))}
            </div>
            <div className="cv-bunting" aria-hidden="true">
              {Array.from({ length: 14 }).map((_, i) => (
                <span key={i} className={i % 2 ? "cv-fl cv-fl-w" : "cv-fl"} />
              ))}
            </div>
            <TitleMark />
            <div className="cv-signs">
              <span className="cv-sign brass">{t("menu_welcome")}</span>
              <span className="cv-sign red">{t("menu_allwin")}</span>
            </div>
            <button className="cv-nameplate" onClick={() => setAskName(true)}>
              {getName() || t("name_ph")} <span className="cv-pen">✎</span>
            </button>
          </header>

          <main className="cv-tiles">
            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/horse.glb" thumb="/images/thumbs/horse.png" fallback="🏇" static size={104} /></span>
                <span className="cv-tname">{t("tile_horse")}</span>
                <button className="cv-tgo" onClick={() => play("horse")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/football.glb?v=2" thumb="/images/thumbs/football.png" fallback="⚽" /></span>
                <span className="cv-tname">{t("tile_football")}</span>
                <button className="cv-tgo" onClick={() => play("football")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/basketball.glb?v=10" thumb="/images/thumbs/basketball.png" fallback="🏀" orbit="90deg 78deg 105%" orbitLock={false} spinDeg={-26} /></span>
                <span className="cv-tname">{t("tile_basket")}</span>
                <button className="cv-tgo" onClick={() => play("basket")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/target.glb" thumb="/images/thumbs/target.png" fallback="🎯" static animated size={104} orbit="0deg 80deg 140%" /></span>
                <span className="cv-tname">{t("tile_shoot")}</span>
                <button className="cv-tgo" onClick={() => play("shoot")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/claw.glb" thumb="/images/thumbs/claw.png" fallback="🕹️" static animated size={104} orbit="0deg 88deg 105%" /></span>
                <span className="cv-tname">{t("tile_claw")}</span>
                <button className="cv-tgo" onClick={() => play("claw")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/wheel.glb" thumb="/images/thumbs/wheel.png" fallback="🎡" static spinOnHover size={96} /></span>
                <span className="cv-tname">{t("tile_wheel")}</span>
                <button className="cv-tgo" onClick={() => play("wheel")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/racecar.glb" thumb="/images/thumbs/racecar.png" fallback="🏎️" size={110} /></span>
                <span className="cv-tname">{t("tile_race")}</span>
                <button className="cv-tgo" onClick={() => play("race")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/mole.glb" thumb="/images/thumbs/mole.png" fallback="🦡" static animated size={122} orbit="0deg 82deg 118%" target="0.12m 0.15m 0m" /></span>
                <span className="cv-tname">{t("tile_mole")}</span>
                <button className="cv-tgo" onClick={() => play("mole")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/can_tower.glb" thumb="/images/thumbs/can_tower.png" fallback="🥫" static animated size={106} orbit="0deg 85deg 120%" /></span>
                <span className="cv-tname">{t("tile_cans")}</span>
                <button className="cv-tgo" onClick={() => play("cans")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/teddy_balance.glb?v=2" thumb="/images/thumbs/teddy_balance.png" fallback="🤸" static animated size={110} orbit="0deg 85deg 106%" /></span>
                <span className="cv-tname">{t("tile_hang")}</span>
                <button className="cv-tgo" onClick={() => play("hang")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                {/* komposit: Meshy-sæk + teddy_standing m. indbagt kig-frem-loop;
                    orbit 130% = kamera-margin, så bamsen ikke klippes i yderposition */}
                <span className="cv-temoji"><Model3D src="/models/teddy_punchbag.glb" thumb="/images/thumbs/teddy_punchbag.png" fallback={<PunchIcon size={96} />} static animated size={106} orbit="0deg 85deg 130%" /></span>
                <span className="cv-tname">{t("tile_punch")}</span>
                <button className="cv-tgo" onClick={() => play("punch")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                {/* Meshy v4 (helt ren and); orange bund-cirkel malet ud UV-masket */}
                <span className="cv-temoji"><Model3D src="/models/duck.glb?v=2" thumb="/images/thumbs/duck.png" fallback={<DuckIcon size={96} />} size={100} orbit="35deg 78deg 105%" orbitLock={false} /></span>
                <span className="cv-tname">{t("tile_duck")}</span>
                <button className="cv-tgo" onClick={() => play("duck")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                {/* Meshy-stak omfarvet til guld i post (kom beige ud — klo-mønstret) */}
                <span className="cv-temoji"><Model3D src="/models/coin_pusher.glb" thumb="/images/thumbs/coin_pusher.png" fallback={<PusherIcon size={96} />} size={100} orbit="15deg 72deg 105%" orbitLock={false} /></span>
                <span className="cv-tname">{t("tile_pusher")}</span>
                <button className="cv-tgo" onClick={() => play("pusher")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><HammerIcon size={96} /></span>
                <span className="cv-tname">{t("tile_hammer")}</span>
                <button className="cv-tgo" onClick={() => play("hammer")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                {/* komposit: Meshy-radiobil + siddende teddy i sædet (klomaskine-mønstret) */}
                <span className="cv-temoji"><Model3D src="/models/teddy_bumpercar.glb" thumb="/images/thumbs/teddy_bumpercar.png" fallback={<BumpIcon size={96} />} size={104} orbit="245deg 70deg 108%" orbitLock={false} /></span>
                <span className="cv-tname">{t("tile_bump")}</span>
                <button className="cv-tgo" onClick={() => play("bump")}>{t("play")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><Model3D src="/models/teddy_wave.glb?v=3" thumb="/images/thumbs/teddy_wave.png" fallback="🧸" static animated size={112} orbit="0deg 85deg 150%" /></span>
                <span className="cv-tname">{t("tile_prizes")}</span>
                <button className="cv-tgo gold" onClick={() => play("prizes")}>
                  {prizeCount} {prizeCount === 1 ? t("prizes_one") : t("prizes_many")}
                </button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji"><CoinImg px={96} className="cv-flat" /></span>
                <span className="cv-tname">{t("tile_exchange")}</span>
                <button className="cv-tgo gold" onClick={() => play("exchange")}>{t("ex_go")}</button>
              </div>
            </div>

            <div className="cv-tile">
              <div className="cv-panel">
                <span className="cv-temoji">
                  <span className="cv-glimmer">
                    <Model3D src="/models/trophy.glb?v=2" thumb="/images/thumbs/trophy.png" fallback="🏆" size={86} />
                    <i className="cv-spark s1">✦</i>
                    <i className="cv-spark s2">✦</i>
                    <i className="cv-spark s3">✧</i>
                    <i className="cv-spark s4">✦</i>
                    <i className="cv-spark s5">✧</i>
                  </span>
                </span>
                <span className="cv-tname">{t("tile_board")}</span>
                <button className="cv-tgo gold" onClick={() => play("board")}>{t("board_go")}</button>
              </div>
            </div>
          </main>

          <footer className="cv-footer">
            © 2026 Teddy Chase · <a href={lang === "da" ? "/privacy/" : `/privacy/${lang}.html`}>{t("footer_privacy")}</a>
          </footer>
        </div>
      )}

      {askName && (
        <NameModal
          onDone={() => {
            setAskName(false);
            syncScore();   // a renamed device keeps its single board spot
            if (pending) { const p = pending; setPending(null); go(p); }
          }}
          onCancel={() => { setAskName(false); setPending(null); }}
        />
      )}
    </div>
  );
}

const css = `
.cv-root{
  position:relative; min-height:100vh; overflow:hidden;
  background:#1c3317;
  background-image:
    radial-gradient(120% 90% at 50% -12%, #3f6b30 0%, #23421d 46%, #14260f 100%);
  font-family:'Nunito',system-ui,sans-serif; color:#F6EED9;
  display:flex; flex-direction:column; align-items:center; padding-bottom:30px;
  -webkit-user-select:none; user-select:none;
}
.cv-scene{
  position:absolute; inset:0; pointer-events:none; z-index:0;
  background-image:
    radial-gradient(circle at 8% 30%, rgba(120,160,255,.10) 0 3px, transparent 4px),
    radial-gradient(circle at 92% 34%, rgba(255,150,90,.12) 0 4px, transparent 5px),
    radial-gradient(circle at 88% 26%, rgba(255,220,120,.10) 0 3px, transparent 4px),
    radial-gradient(circle at 12% 44%, rgba(255,120,120,.08) 0 3px, transparent 4px),
    repeating-linear-gradient(115deg, rgba(255,255,255,.02) 0 2px, transparent 2px 9px);
  mask-image:radial-gradient(120% 80% at 50% 0%, #000 40%, transparent 78%);
}
.cv-langbox{position:absolute; top:10px; right:10px; z-index:5;}
.cv-flag{
  line-height:0; padding:7px 8px; cursor:pointer;
  background:rgba(0,0,0,.3); border:1px solid rgba(255,255,255,.25); border-radius:10px;
}
.cv-flag svg{border-radius:3px; box-shadow:0 1px 3px rgba(0,0,0,.4); display:block;}
.cv-flag:active{transform:scale(.94);}
.cv-langmenu{
  position:absolute; top:calc(100% + 6px); right:0; display:flex; flex-direction:column; gap:2px;
  background:rgba(15,8,20,.96); border:1px solid rgba(255,255,255,.25); border-radius:12px;
  padding:6px; box-shadow:0 10px 30px rgba(0,0,0,.5);
}
.cv-langitem{
  display:flex; align-items:center; gap:10px; padding:6px 12px 6px 8px; cursor:pointer;
  background:none; border:none; border-radius:8px; font-family:inherit;
  font-weight:800; font-size:13px; color:#F6EED9; white-space:nowrap; text-align:left;
}
.cv-langitem svg{border-radius:3px; box-shadow:0 1px 3px rgba(0,0,0,.4); flex:none;}
.cv-langitem:hover{background:rgba(255,255,255,.12);}
.cv-langitem.on{background:rgba(255,255,255,.18);}
.cv-booth{position:relative; z-index:1; width:100%; max-width:540px; text-align:center; padding:8px 12px 6px;}
.cv-lights{display:flex; justify-content:center; gap:0; height:12px; margin-bottom:2px;}
.cv-bulb{
  width:5px; height:5px; margin:0 8px; border-radius:50%; align-self:center;
  background:#fff6d8; box-shadow:0 0 5px 1px rgba(255,225,150,.9), 0 0 10px 2px rgba(255,190,90,.5);
}
.cv-bulb.warm{background:#ffd77a; box-shadow:0 0 6px 2px rgba(255,190,90,.9);}
.cv-bunting{display:flex; justify-content:center; gap:1px; margin-bottom:8px;}
.cv-fl{
  width:0;height:0;border-left:11px solid transparent;border-right:11px solid transparent;
  border-top:17px solid #C8352E; filter:drop-shadow(0 3px 2px rgba(0,0,0,.4));
}
.cv-fl-w{border-top-color:#EFE6CE;}
.cv-title{position:relative; margin:0 0 2px;}
.cv-title-img{
  display:block; width:100%; max-width:520px; margin:0 auto; height:auto;
  mix-blend-mode:screen;
  filter:drop-shadow(0 4px 10px rgba(0,0,0,.5));
}
/* retro marquee: gold-gradient letters, a bright neon-tube core inside each
   glyph (::before stroke twin), warm halo and a soft mains flicker */
.cv-title-text{
  position:relative; font-family:'Alfa Slab One',Georgia,serif;
  font-size:clamp(30px,9.5vw,58px); line-height:.95; margin:0;
  letter-spacing:2px; white-space:nowrap;
  background:linear-gradient(180deg, #fffbe8 0%, #ffedb0 32%, #f5c94e 68%, #d99a26 100%);
  -webkit-background-clip:text; background-clip:text; color:transparent;
  filter:
    drop-shadow(0 0 10px rgba(255,205,100,.5))
    drop-shadow(0 0 26px rgba(255,170,50,.3))
    drop-shadow(0 4px 0 rgba(96,64,14,.95))
    drop-shadow(0 7px 1px rgba(58,38,8,.9))
    drop-shadow(0 11px 14px rgba(0,0,0,.55));
  animation:cv-marquee-flicker 3.4s ease-in-out infinite;
}
.cv-title-text::before{
  content:attr(data-text); position:absolute; inset:0; z-index:1;
  color:transparent; -webkit-text-stroke:1.6px rgba(255,252,235,.85);
  filter:drop-shadow(0 0 3px rgba(255,240,190,.8));
  pointer-events:none;
}
@keyframes cv-marquee-flicker{
  0%, 100% { opacity:1; }
  92% { opacity:1; }
  94% { opacity:.82; }
  96% { opacity:1; }
  98% { opacity:.9; }
}
.cv-signs{display:flex; gap:10px; justify-content:center; margin-top:10px; flex-wrap:wrap;}
.cv-sign{
  position:relative; font-weight:800; font-size:12.5px; letter-spacing:.5px;
  padding:7px 16px; border-radius:5px;
}
.cv-sign.brass{
  color:#5a4410; text-shadow:0 1px 0 rgba(255,255,255,.35);
  background:linear-gradient(#f4e2a4, #c9a44e 55%, #a9832f 100%);
  border:1px solid #8a6c26;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.6), inset 0 -3px 6px rgba(120,90,20,.4), 0 4px 8px rgba(0,0,0,.4);
}
.cv-sign.brass::before, .cv-sign.brass::after{
  content:""; position:absolute; top:50%; width:5px; height:5px; margin-top:-2.5px;
  border-radius:50%; background:radial-gradient(circle at 40% 35%, #fff6cf, #8a6c26);
  box-shadow:inset 0 0 0 1px rgba(90,70,20,.6);
}
.cv-sign.brass::before{left:6px;} .cv-sign.brass::after{right:6px;}
.cv-sign.red{
  color:#fff4ec; text-shadow:0 1px 2px rgba(0,0,0,.5);
  background:linear-gradient(#d6473a, #a81f18);
  border:1px solid #7a1811;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.35), inset 0 -3px 7px rgba(90,10,5,.5), 0 4px 8px rgba(0,0,0,.4);
}
.cv-nameplate{
  margin-top:10px; font-family:inherit; font-weight:800; font-size:13px; cursor:pointer;
  color:#F6EED9; background:rgba(0,0,0,.28); border:1px solid rgba(255,255,255,.22);
  padding:6px 14px; border-radius:18px;
}
.cv-pen{opacity:.6; margin-left:2px;}
.cv-tiles{
  position:relative; z-index:1; width:min(94%,460px);
  display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); grid-auto-rows:1fr;
  gap:14px; margin-top:16px;
}
.cv-tile, .cv-panel{min-width:0;}
.cv-temoji > *{max-width:100%;}
.cv-tile{
  display:flex; padding:8px; border-radius:18px;
  background:linear-gradient(150deg, #8a5c33 0%, #5f3d1f 52%, #3f2812 100%);
  box-shadow:
    inset 0 2px 1px rgba(255,214,150,.5),
    inset 0 -4px 6px rgba(0,0,0,.5),
    0 8px 16px rgba(0,0,0,.5);
}
.cv-panel{
  flex:1; display:flex; flex-direction:column; align-items:center; justify-content:space-between; gap:6px;
  padding:16px 10px 14px; border-radius:12px;
  background:linear-gradient(158deg, #f8f1de 0%, #ece0c2 60%, #ddceaa 100%);
  box-shadow:
    inset 0 2px 3px rgba(255,255,255,.85),
    inset 0 -8px 16px rgba(120,95,55,.3),
    inset 0 0 0 1px rgba(120,95,55,.25);
}
.cv-temoji{
  font-size:clamp(40px,11vw,52px); line-height:1; height:114px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center;
  filter:drop-shadow(0 6px 5px rgba(0,0,0,.4)) drop-shadow(0 2px 1px rgba(0,0,0,.3));
}
.cv-temoji model-viewer{filter:none; overflow:visible;}
.cv-flat{filter:drop-shadow(0 4px 4px rgba(0,0,0,.35));}
/* gold glimmer: warm glow behind the trophy + staggered twinkling sparks */
.cv-glimmer{position:relative; display:inline-block; line-height:1;}
.cv-glimmer::before{
  content:""; position:absolute; inset:8%; border-radius:50%; pointer-events:none;
  background:radial-gradient(circle, rgba(255,214,110,.45) 0%, rgba(255,190,60,.18) 45%, transparent 70%);
  animation:cv-glowpulse 2.6s ease-in-out infinite;
}
.cv-spark{
  position:absolute; font-style:normal; color:#ffe9a8; pointer-events:none;
  text-shadow:0 0 6px #ffd75e, 0 0 12px rgba(255,190,60,.8);
  animation:cv-twinkle 2.2s ease-in-out infinite; opacity:0;
}
.cv-spark.s1{top:4%;  left:12%; font-size:15px; animation-delay:0s;}
.cv-spark.s2{top:22%; right:6%; font-size:11px; animation-delay:.5s;}
.cv-spark.s3{bottom:26%; left:2%; font-size:12px; animation-delay:1s;}
.cv-spark.s4{top:52%; right:14%; font-size:15px; animation-delay:1.4s;}
.cv-spark.s5{bottom:6%; right:30%; font-size:10px; animation-delay:1.8s;}
@keyframes cv-twinkle{
  0%,100%{opacity:0; transform:scale(.4) rotate(0deg);}
  50%{opacity:1; transform:scale(1.15) rotate(25deg);}
}
@keyframes cv-glowpulse{
  0%,100%{opacity:.65; transform:scale(.96);}
  50%{opacity:1; transform:scale(1.05);}
}
.cv-tname{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(13px,3.4vw,17px);
  letter-spacing:.3px; color:#5a3a20; text-align:center; line-height:1.05;
  min-height:38px; display:flex; align-items:center; justify-content:center; flex-shrink:0;
  text-shadow:0 1px 0 rgba(255,255,255,.7), 0 -1px 1px rgba(90,58,32,.4), 0 2px 3px rgba(0,0,0,.15);
}
.cv-tgo{
  display:inline-flex; align-items:center; justify-content:center; gap:6px; margin-top:5px;
  font-family:inherit; font-size:12px; font-weight:800; letter-spacing:.6px; cursor:pointer;
  color:#fff; text-shadow:0 1px 2px rgba(120,10,5,.7);
  padding:9px 14px; border-radius:20px; white-space:nowrap;
  max-width:100%; overflow:hidden; text-overflow:ellipsis;
  background:linear-gradient(#e5563f, #b6241b);
  border:2px solid #c8a24a;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.55), inset 0 -3px 5px rgba(90,10,5,.5), 0 4px 7px rgba(0,0,0,.4);
  transition:transform .1s ease, box-shadow .1s ease;
}
.cv-tgo:hover{transform:translateY(-1px); filter:brightness(1.06);}
.cv-tgo:active{transform:translateY(2px); box-shadow:inset 0 1px 0 rgba(255,255,255,.4), inset 0 -2px 4px rgba(90,10,5,.5), 0 2px 3px rgba(0,0,0,.4);}
.cv-tgo.gold{
  color:#3c2c07; text-shadow:0 1px 0 rgba(255,255,255,.4);
  background:linear-gradient(#f0d67a, #bd9127);
  border-color:#7c5d16;
}
/* name modal */
.cv-overlay{
  position:fixed; inset:0; background:rgba(10,20,8,.78); z-index:40;
  display:flex; align-items:center; justify-content:center; padding:14px;
  font-family:'Nunito',system-ui,sans-serif;
}
.cv-card{
  width:min(92%,360px); background:#F6EED9; color:#22301c;
  border-radius:14px; padding:0 20px 20px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.cv-ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:19px; letter-spacing:.5px;
  background:#C8352E; color:#fff; margin:0 -20px 14px; padding:13px;
  border-radius:14px 14px 0 0;
}
.cv-namenote{font-size:12.5px; font-weight:700; opacity:.75; margin:0 0 12px;}
.cv-nameinput{
  width:100%; box-sizing:border-box; font-family:inherit; font-size:18px; font-weight:800;
  text-align:center; padding:11px; border-radius:10px; border:2px solid #d8cfae;
  background:#fff; color:#22301c; outline:none;
}
.cv-nameinput:focus{border-color:#C8352E;}
.cv-nameerr{color:#a81f18; font-size:12.5px; font-weight:800; margin:8px 0 0;}
.cv-cta{
  width:100%; margin-top:14px; padding:12px; font-size:16px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:#C8352E; color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cv-cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cv-cta.alt{background:#5c7a4a; box-shadow:0 4px 0 #37492c; margin-top:8px; font-size:13px; padding:9px;}
.cv-footer{
  position:relative; z-index:1; text-align:center; margin:18px 0 6px;
  font-size:12px; font-weight:700; color:rgba(246,238,217,.65); letter-spacing:.4px;
}
.cv-footer a{color:rgba(246,238,217,.85); text-decoration:underline;}
@media (max-width:340px){ .cv-tiles{grid-template-columns:1fr;} }
`;
